/**
 * The platform console's "Consultant limits" panel, its logic (migration 0201; the routes are
 * /v1/admin/orgs/{org_id}/copilot-limits and its /reset): the limits form and its PUT body, each limit's source in
 * words, the counts in words, the reset's confirmation words, and the server's refusals in plain words. Pure.
 *
 * A person may send the AI Casting Consultant a set number of messages in any 24 hours, and the whole org a set
 * number. Each limit is the org's own when a platform admin set one, else the platform's (the code's default, or the
 * server's environment). A reset starts a count again, a person's or the whole org's; it never deletes a message.
 */
import { format } from "date-fns";
import type { CopilotLimit, CopilotLimits, CopilotLimitsPut, CopilotUsage } from "@/lib/api/platform-admin";
import { errorCodeOf, errorDetailOf, isNetworkError, statusOf } from "@/lib/api/error";

/** The most either limit may be set to (the server's CHECK); the least is 1. */
export const COPILOT_DAILY_MAX = 100000;

/** The query key the panel reads the org's limits under. */
export const copilotLimitsKey = (orgId: string) => ["platform-org-copilot-limits", orgId] as const;

/** What the limits form holds: each limit as typed, "" for the platform's. */
export interface LimitsForm {
  user: string;
  org: string;
}

/** The form the stored limits read as: the org's own number, or "" where the platform's applies. */
export function formOfLimits(v: Pick<CopilotLimits, "user_daily" | "org_daily">): LimitsForm {
  const own = (l: CopilotLimit) => (l.source === "org" ? String(l.limit) : "");
  return { user: own(v.user_daily), org: own(v.org_daily) };
}

const NUMBER_WORDS = `Enter a whole number from 1 to ${COPILOT_DAILY_MAX.toLocaleString("en-US")}, or leave it empty for the platform's limit.`;

/** One field of the form as the PUT sends it: null for empty, else a whole number in range, else undefined. */
function limitOf(raw: string): number | null | undefined {
  const s = raw.trim().replace(/,/g, "");
  if (s === "") return null;
  if (!/^\d+$/.test(s)) return undefined;
  const n = Number(s);
  return n >= 1 && n <= COPILOT_DAILY_MAX ? n : undefined;
}

/** The PUT body the form makes, or what is wrong with it in words. */
export function limitsBody(f: LimitsForm): { body: CopilotLimitsPut } | { error: string } {
  const user = limitOf(f.user);
  if (user === undefined) return { error: `Per person: ${NUMBER_WORDS}` };
  const org = limitOf(f.org);
  if (org === undefined) return { error: `Whole org: ${NUMBER_WORDS}` };
  return { body: { user_daily: user, org_daily: org } };
}

/** Whether the form differs from what is stored. */
export function limitsChanged(f: LimitsForm, v: Pick<CopilotLimits, "user_daily" | "org_daily">): boolean {
  const made = limitsBody(f);
  if ("error" in made) return true;
  const stored = (l: CopilotLimit) => (l.source === "org" ? l.limit : null);
  return made.body.user_daily !== stored(v.user_daily) || made.body.org_daily !== stored(v.org_daily);
}

/** "a day" for a count: "1 message" or "12 messages". */
export function messagesWords(n: number): string {
  return n === 1 ? "1 message" : `${n.toLocaleString("en-US")} messages`;
}

/** A limit and where it comes from: "80 a day, this org's own (the platform's is 50)". */
export function limitWords(l: CopilotLimit): string {
  const n = l.limit.toLocaleString("en-US");
  switch (l.source) {
    case "org":
      return `${n} a day, this org's own (the platform's is ${l.platform.toLocaleString("en-US")})`;
    case "env":
      return `${n} a day, the platform's, set on the server`;
    default:
      return `${n} a day, the platform's default`;
  }
}

/** What an empty field falls back to: "Empty: the platform's 50". */
export function platformPlaceholder(l: CopilotLimit): string {
  return `Empty: the platform's ${l.platform.toLocaleString("en-US")}`;
}

/**
 * Where a count starts, in words: "in the last 24 hours", or "since the reset at 5 Oct 14:05" when a reset moved its
 * start (more than a minute after a day before now).
 */
export function sinceWords(since: string, now: string): string {
  const s = new Date(since).getTime();
  const n = new Date(now).getTime();
  if (Number.isNaN(s) || Number.isNaN(n) || s - (n - 24 * 3600 * 1000) <= 60 * 1000) return "in the last 24 hours";
  return `since the reset at ${format(new Date(since), "d MMM HH:mm")}`;
}

/** A count against its limit: "12 of 200 messages in the last 24 hours". */
export function usageWords(count: number, limit: number, since: string, now: string): string {
  return `${count.toLocaleString("en-US")} of ${messagesWords(limit)} ${sinceWords(since, now)}`;
}

/** How the console names a person: their display name, else their email. */
export function personName(p: Pick<CopilotUsage, "display_name" | "email">): string {
  return p.display_name.trim() || p.email;
}

/** What a reset is for: the whole org, or one person. */
export type ResetTarget = { kind: "org" } | { kind: "person"; userId: string; name: string };

/** The button that asks for a reset, in words. */
export function resetButtonWords(t: ResetTarget): string {
  return t.kind === "org" ? "Reset for this org" : `Reset for ${t.name}`;
}

/** The in-page confirmation's question: what the reset does, in plain words. */
export function resetConfirmWords(t: ResetTarget, orgName: string): string {
  if (t.kind === "org") {
    return (
      `Reset the consultant's daily count for ${orgName} and everyone in it? The messages sent so far stop counting ` +
      "against the org's limit and each person's. Nothing is deleted."
    );
  }
  return (
    `Reset the consultant's daily count for ${t.name} in ${orgName}? Their messages so far stop counting against their ` +
    "own limit; the org's count stays as it is. Nothing is deleted."
  );
}

/** What the panel says once a reset is made. */
export function resetDoneWords(t: ResetTarget, orgName: string): string {
  return t.kind === "org"
    ? `Reset. Every count in ${orgName} starts again from now.`
    : `Reset. ${t.name}'s count starts again from now.`;
}

/** A reset in the list: "5 Oct 14:05: the whole org, by ops@example.test. Demo day." */
export function resetLineWords(r: {
  user_id: string | null;
  email: string;
  display_name: string;
  reset_at: string;
  reset_by: string;
  note: string;
}): string {
  const when = format(new Date(r.reset_at), "d MMM HH:mm");
  const who = r.user_id === null ? "the whole org" : personName(r);
  const by = r.reset_by ? `, by ${r.reset_by}` : "";
  const note = r.note.trim() ? ` ${/[.!?]$/.test(r.note.trim()) ? r.note.trim() : `${r.note.trim()}.`}` : "";
  return `${when}: ${who}${by}.${note}`;
}

/** The server's refusal of a read or a write, in plain words. */
export function limitsErrorWords(err: unknown, orgName: string): string {
  const status = statusOf(err);
  const code = errorCodeOf(err);
  if (status === 401) return "Your session has ended: sign in again.";
  if (status === 403) return "Only a platform admin can see or change the consultant's limits.";
  if (code === "org_not_found") return `${orgName} no longer exists.`;
  if (code === "user_not_in_org") return `That person is no longer a member of ${orgName}, so there is no count to reset.`;
  if (status === 400) return errorDetailOf(err) ?? "The server did not take that. Check the numbers and try again.";
  if (isNetworkError(err)) return "The platform could not be reached. Check the connection and try again.";
  return "Something went wrong on the platform. Try again in a moment.";
}
